<template>
  <b-card v-if="spotData">
    <b-card-title class="mb-0">
      Price History
    </b-card-title>
    <div class="font-small-3 mb-1 text-muted">
      Historical spot price performance. This price has been rebased.
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <app-echart-line
        v-else
        :key="chartKey"
        :option-data="chartData"
      />
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText,
} from 'bootstrap-vue'
import AppEchartLine from '@core/components/charts/echart/AppEchartLine.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    AppEchartLine,
    LoadingContainer,
  },
  props: {
    spotData: {
      type: Array,
      default: null,
    },
    currency: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      chartKey: 0,
      chartData: {
        tooltip: {},
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    yAxisLabel() {
      return this.currency ? `Price (${this.currency})` : 'Price'
    },
  },
  watch: {
    spotData: {
      handler(data) {
        if (data && data.length > 0) {
          this.processSpotData(data)
        }
      },
      immediate: true,
    },
    skin() {
      // Re-process data when theme changes
      if (this.spotData && this.spotData.length > 0) {
        this.processSpotData(this.spotData)
      }
    },
  },
  methods: {
    processSpotData(seriesData) {
      // Find min and max values for better y-axis scaling
      let minValue = Infinity
      let maxValue = -Infinity
      seriesData.forEach(series => {
        series.data.forEach(point => {
          if (point.y < minValue) minValue = point.y
          if (point.y > maxValue) maxValue = point.y
        })
      })

      // Calculate y-axis range with some padding
      const range = maxValue - minValue
      const yAxisMin = Math.floor(minValue - range * 0.1)
      const yAxisMax = Math.ceil(maxValue + range * 0.1)

      // Convert to ECharts format
      const colors = ['#ff9f43', '#1a73e8', '#ea5455', '#28c76f', '#00cfe8']
      const echartsSeries = seriesData.map((series, index) => ({
        name: series.name,
        type: 'line',
        data: series.data.map(point => [point.x, point.y]),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2,
          color: colors[index % colors.length],
        },
        itemStyle: {
          color: colors[index % colors.length],
        },
      }))

      this.chartData = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
          },
          formatter: params => {
            const date = new Date(params[0].value[0])
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            let result = `${dateStr}<br/>`
            params.forEach(param => {
              const value = param.value[1].toFixed(2)
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`
            })
            return result
          },
        },
        legend: {
          data: echartsSeries.map(s => s.name),
          top: 10,
        },
        grid: {
          left: 70,
          right: 50,
          bottom: 70,
          top: 60,
          containLabel: false,
        },
        xAxis: {
          type: 'time',
          name: 'Date',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter(value) {
              const date = new Date(value)
              return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            },
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: this.yAxisLabel,
          nameLocation: 'middle',
          nameGap: 50,
          min: yAxisMin,
          max: yAxisMax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: value => value.toFixed(2),
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: echartsSeries,
      }
      this.loading = false
      // Increment key to force chart re-render
      this.chartKey += 1
    },
  },
}
</script>
